import { Icon } from '@iconify/react'
import type { RouletteProbabilityBudget } from '@/lib/rouletteProbabilityBudget'
import styles from './CampanhasPage.module.css'

export interface RoletaProbabilitySummaryProps {
  budget: RouletteProbabilityBudget
  positionsCount: number
}

export function RoletaProbabilitySummary({ budget, positionsCount }: RoletaProbabilitySummaryProps) {
  const statusClass =
    budget.status === 'exact'
      ? styles.roletaProbSummaryExact
      : budget.status === 'over'
        ? styles.roletaProbSummaryOver
        : budget.status === 'under'
          ? styles.roletaProbSummaryUnder
          : styles.roletaProbSummaryEmpty

  const progressClass =
    budget.status === 'exact'
      ? styles.roletaProbProgressFillExact
      : budget.status === 'over'
        ? styles.roletaProbProgressFillOver
        : styles.roletaProbProgressFillUnder

  return (
    <div
      className={`${styles.roletaProbSummary} ${statusClass}`}
      role="status"
      aria-live="polite"
      aria-label={`Distribuição de probabilidades: ${budget.totalDisplay} por cento de 100`}
    >
      <div className={styles.roletaProbSummaryHeader}>
        <Icon
          icon={
            budget.status === 'exact'
              ? 'mdi:check-circle-outline'
              : budget.status === 'over'
                ? 'mdi:alert-circle-outline'
                : 'mdi:chart-pie'
          }
          aria-hidden
        />
        <div>
          <p className={styles.roletaProbSummaryTitle}>Distribuição das probabilidades</p>
          <p className={styles.roletaProbSummarySubtitle}>
            {positionsCount} posições · a soma deve ser exatamente 100%
          </p>
        </div>
      </div>

      <div className={styles.roletaProbSummaryGrid}>
        <div className={styles.roletaProbSummaryMetric}>
          <span className={styles.roletaProbSummaryMetricLabel}>Total distribuído</span>
          <strong className={styles.roletaProbSummaryMetricValue}>{budget.totalDisplay}%</strong>
        </div>
        <div className={styles.roletaProbSummaryMetric}>
          <span className={styles.roletaProbSummaryMetricLabel}>{budget.remainderLabel}</span>
          <strong className={styles.roletaProbSummaryMetricValue}>
            {budget.status === 'exact' ? '—' : `${budget.remainderDisplay}%`}
          </strong>
        </div>
        <div className={styles.roletaProbSummaryMetric}>
          <span className={styles.roletaProbSummaryMetricLabel}>Meta</span>
          <strong className={styles.roletaProbSummaryMetricValue}>100%</strong>
        </div>
      </div>

      <div className={styles.roletaProbProgressTrack} aria-hidden>
        <div
          className={`${styles.roletaProbProgressFill} ${progressClass}`}
          style={{ width: `${budget.progressPercent}%` }}
        />
        {budget.status === 'over' && (
          <div
            className={styles.roletaProbProgressOverflow}
            style={{ width: `${Math.min(100, Math.max(0, budget.totalPercent - 100))}%` }}
          />
        )}
      </div>

      {budget.status === 'over' && (
        <p className={styles.roletaProbSummaryMessage}>
          A soma passou de 100%. Ajuste as fatias ou use &quot;Usar restante&quot; em uma posição para
          equilibrar.
        </p>
      )}
      {budget.status === 'under' && budget.totalPercent > 0 && (
        <p className={styles.roletaProbSummaryMessage}>
          Faltam <strong>{budget.remainderDisplay}%</strong> para completar 100%. Use &quot;Usar restante&quot;
          em uma fatia ou distribua manualmente.
        </p>
      )}
      {budget.status === 'empty' && (
        <p className={styles.roletaProbSummaryMessage}>
          Informe a probabilidade de cada posição. Use vírgula para decimais (ex.: 0,005).
        </p>
      )}
    </div>
  )
}
